import { useEffect, useState } from "react";
import { io, type Socket } from "socket.io-client";

type UseWebControlRoomSocketOptions = {
  websocketUrl: string;
  token?: string | null;
  enabled?: boolean;
};

/**
 * Creates a public websocket connection dedicated to Control Room target
 * publishing for runtime widget/display pages.
 *
 * Runtime targets only need to publish capabilities and receive shortcut
 * signals, both of which are intentionally public for OBS/Streamlabs usage.
 *
 * @param options - Connection URL, optional token (ignored) and enable flag.
 * @returns The live socket instance, or null when disabled.
 */
export function useWebControlRoomSocket({
  websocketUrl,
  token: _token,
  enabled = true,
}: UseWebControlRoomSocketOptions): Socket | null {
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    if (!enabled || !websocketUrl) {
      setSocket(null);
      return;
    }

    const nextSocket = io(websocketUrl, {
      transports: ["websocket"],
      query: { clientType: "WEBAPP" },
    });

    setSocket(nextSocket);

    return () => {
      nextSocket.disconnect();
      setSocket((currentSocket) =>
        currentSocket === nextSocket ? null : currentSocket,
      );
    };
  }, [enabled, websocketUrl]);

  return socket;
}
